'use client'

import { useMemo } from 'react'
import { useDocumentView } from '@/document'

export interface TopicWithTaskCount {
  id: string
  name: string
  count: number
}

/**
 * Computes active task counts per topic from the Automerge document.
 * Returns sorted topics (by count descending, then alphabetically)
 * and the total active task count across all topics.
 */
export function useTaskTopicCounts() {
  const doc = useDocumentView()

  return useMemo(() => {
    if (!doc) return { topics: [] as TopicWithTaskCount[], totalTaskCount: 0 }

    const topicTaskCounts = new Map<string, number>()
    let total = 0

    for (const entry of Object.values(doc.entries)) {
      if (entry.type !== 'task') continue
      if (entry.archivedAt) continue

      const metadata = entry.metadata as { status?: string }
      if (metadata.status === 'done' || metadata.status === 'someday') continue

      total++

      for (const topicId of entry.topicIds) {
        topicTaskCounts.set(topicId, (topicTaskCounts.get(topicId) || 0) + 1)
      }
    }

    const topics: TopicWithTaskCount[] = []

    for (const [topicId, count] of topicTaskCounts) {
      const topic = doc.topics[topicId]
      if (!topic || topic.archivedAt) continue
      if (topic.entryType !== 'task') continue

      topics.push({ id: topic.id, name: topic.name, count })
    }

    topics.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))

    return { topics, totalTaskCount: total }
  }, [doc])
}
